/**
 * QAILogo Component (v5.0 - PHASE 5.1 Institutional-Grade)
 * Reusable logo for Q AI branding with proportional sizing
 *
 * DESIGN RULE: Q.ai text height = Green box height (proportional sizing)
 * PHASE 5.1: Standard size is 32x32px for institutional-grade consistency
 *
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero'
 * @param {boolean} animated - Enable pulse animation
 * @param {boolean} showText - Show brand text next to logo
 * @param {string} textVariant - 'full' = "Q.ai" styled | 'short' = "Q AI"
 * @param {boolean} useSVG - Use SVG files instead of generated graphics (future)
 *
 * SVG REPLACEMENT GUIDE:
 * 1. Create SVG files in src/assets/logos/:
 *    - q-icon-{size}.svg (green box with Q)
 *    - qai-wordmark-{size}.svg (Q.ai text)
 * 2. Set useSVG={true} on the component
 * 3. Component will automatically use SVG files when available
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { QAI_COLORS, QAI_FONTS } from '../../theme/qaiDesignTokens';

// ═══════════════════════════════════════════════════════════════════════════
// SIZE PRESETS - PHASE 5.1: Institutional-Grade Logo System
// Standard: 32x32px (sm) for headers and navigation
// Rule: Q.ai text visual height ≈ Green box height for balanced appearance
// ═══════════════════════════════════════════════════════════════════════════
const SIZES = {
  // Extra Small - Mobile headers (28x28) - MOBILE FIX: Bigger for visibility
  xs: { box: 28, text: '2.275rem', inner: '1rem', radius: '6px', gap: 6 },

  // Small - STANDARD: Chat header, dashboard nav (32x32)
  sm: { box: 32, text: '2.6rem', inner: '1.125rem', radius: '8px', gap: 8 },

  // Medium - Standard headers, toolbars (36x36)
  md: { box: 36, text: '2.925rem', inner: '1.25rem', radius: '10px', gap: 10 },

  // Large - Landing page header (42x42)
  lg: { box: 42, text: '3.4125rem', inner: '1.5rem', radius: '10px', gap: 12 },

  // Extra Large - Feature sections (52x52)
  xl: { box: 52, text: '4.225rem', inner: '1.875rem', radius: '12px', gap: 14 },

  // Hero - Landing hero, splash screens (80x80)
  hero: { box: 80, text: '6.5rem', inner: '2.875rem', radius: '16px', gap: 20 }
};

// Legacy size mapping for backward compatibility
const LEGACY_SIZE_MAP = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
  xlarge: 'xl'
};

const QAILogo = ({
  size = 'md',
  animated = false,
  showText = false,
  textVariant = 'full',
  useSVG = false,  // Future: enable SVG file usage
  className = ''
}) => {
  // Handle legacy size names for backward compatibility
  const normalizedSize = LEGACY_SIZE_MAP[size] || size;
  const sizeConfig = SIZES[normalizedSize] || SIZES.md;
  const { box, text, inner, radius, gap } = sizeConfig;

  // Future SVG support placeholder
  // When useSVG is true and SVG files exist, render them instead
  // const iconSrc = useSVG ? `/assets/logos/q-icon-${normalizedSize}.svg` : null;
  // const wordmarkSrc = useSVG ? `/assets/logos/qai-wordmark-${normalizedSize}.svg` : null;

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${gap}px`
      }}
    >
      {/* Green Q Icon Box - PHASE 5.1: Using design tokens */}
      <Box
        sx={{
          width: box,
          height: box,
          borderRadius: radius,
          background: `linear-gradient(135deg, ${QAI_COLORS.legacy.signalBuy} 0%, ${QAI_COLORS.semantic.bullish} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(39, 181, 109, 0.25)',
          flexShrink: 0,
          animation: animated ? 'qaiPulse 2s ease-in-out infinite' : 'none',
          '@keyframes qaiPulse': {
            '0%, 100%': {
              boxShadow: '0 2px 8px rgba(39, 181, 109, 0.25)',
              transform: 'scale(1)'
            },
            '50%': {
              boxShadow: '0 4px 16px rgba(39, 181, 109, 0.4)',
              transform: 'scale(1.02)'
            }
          }
        }}
      >
        <Typography
          sx={{
            color: QAI_COLORS.text.primary,
            fontWeight: 800,
            fontSize: inner,
            fontFamily: QAI_FONTS.primary,
            lineHeight: 1
          }}
        >
          Q
        </Typography>
      </Box>

      {/* Q.ai Wordmark - PHASE 5.1: Proportional to icon height */}
      {showText && (
        <Typography
          sx={{
            color: QAI_COLORS.text.primary,
            fontWeight: 800,
            fontSize: text,
            fontFamily: QAI_FONTS.primary,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            '& .accent': {
              color: QAI_COLORS.legacy.signalBuy,
              fontWeight: 800
            }
          }}
        >
          {textVariant === 'full' ? (
            <>Q<span className="accent">.ai</span></>
          ) : (
            <>Q<span className="accent">.ai</span></>
          )}
        </Typography>
      )}
    </Box>
  );
};

// Export size presets for external reference
export const LOGO_SIZES = SIZES;

export default QAILogo;
